export type TimelineEventKind = 'print' | 'sale' | 'expense'

export interface TimelineEvent {
  id: string
  kind: TimelineEventKind
  date: string  // YYYY-MM-DD
  title: string
  amount: number  // signed; sales positive, prints/expenses negative
}

export interface DayGroup {
  date: string
  events: TimelineEvent[]
  summary: string
}

export function groupEventsByDay(events: TimelineEvent[]): DayGroup[] {
  const byDate = new Map<string, TimelineEvent[]>()
  for (const e of events) {
    const list = byDate.get(e.date) ?? []
    list.push(e)
    byDate.set(e.date, list)
  }
  const dates = Array.from(byDate.keys()).sort((a, b) => b.localeCompare(a))
  return dates.map((date) => {
    const evs = byDate.get(date)!
    return { date, events: evs, summary: summarise(evs) }
  })
}

function summarise(events: TimelineEvent[]): string {
  const counts: Record<TimelineEventKind, number> = { print: 0, sale: 0, expense: 0 }
  for (const e of events) counts[e.kind] += 1
  const parts: string[] = []
  if (counts.print > 0) parts.push(`${counts.print} print${counts.print === 1 ? '' : 's'}`)
  if (counts.sale > 0)  parts.push(`${counts.sale} sale${counts.sale === 1 ? '' : 's'}`)
  if (counts.expense > 0) parts.push(`${counts.expense} expense${counts.expense === 1 ? '' : 's'}`)
  return parts.join(' · ')
}

interface SaleInput { id: string; sale_date: string; gross_amount: number; platform: string; customer_name: string | null }
interface ExpenseInput {
  id: string
  expense_date: string
  amount: number
  description: string | null
  category: string
  linked_spool_id: string | null
}
interface PrintInput { id: string; print_date: string; description: string | null; purpose: string; total_cost: number; units_produced: number }

export function buildEventList(input: {
  sales: SaleInput[]
  expenses: ExpenseInput[]
  prints: PrintInput[]
}): TimelineEvent[] {
  const events: TimelineEvent[] = []
  for (const s of input.sales) {
    events.push({
      id: `s-${s.id}`,
      kind: 'sale',
      date: s.sale_date,
      title: `Sale — ${s.platform}${s.customer_name ? ` · ${s.customer_name}` : ''}`,
      amount: Number(s.gross_amount),
    })
  }
  // Group spool-linked filament expenses by (date, description) — a 4-pack
  // purchased the same day collapses into a single "Sunlu PLA+ Black ×4 —
  // -$50.12" line instead of four nearly-identical entries.
  const filamentBuckets = new Map<string, { date: string; description: string; total: number; count: number; firstId: string }>()
  for (const e of input.expenses) {
    if (e.linked_spool_id == null) {
      // Non-filament expenses stay as individual events.
      events.push({
        id: `e-${e.id}`,
        kind: 'expense',
        date: e.expense_date,
        title: e.description ?? e.category,
        amount: -Number(e.amount),
      })
      continue
    }
    const description = e.description ?? e.category
    const key = `${e.expense_date}|${description}`
    const existing = filamentBuckets.get(key)
    if (existing) {
      existing.total += Number(e.amount)
      existing.count += 1
    } else {
      filamentBuckets.set(key, {
        date: e.expense_date,
        description,
        total: Number(e.amount),
        count: 1,
        firstId: e.id,
      })
    }
  }
  for (const b of filamentBuckets.values()) {
    events.push({
      id: `e-${b.firstId}`,
      kind: 'expense',
      date: b.date,
      title: b.count > 1 ? `${b.description} (×${b.count})` : b.description,
      amount: -b.total,
    })
  }
  for (const p of input.prints) {
    const label = p.description?.trim() || p.purpose
    const suffix = p.units_produced > 1 ? ` (${p.units_produced}×)` : ''
    events.push({
      id: `p-${p.id}`,
      kind: 'print',
      date: p.print_date,
      title: `${label}${suffix}`,
      amount: -Number(p.total_cost),
    })
  }
  events.sort((a, b) => b.date.localeCompare(a.date))
  return events
}
